import React, { useState } from "react";

interface ResolveIssueModalProps {
  issue?: any;
}

const ResolveIssueModal: React.FC<ResolveIssueModalProps> = ({ issue }) => {
  const [resolutionData, setResolutionData] = useState({
    resolutionType: "",
    resolution: "",
    timeSpent: "",
    followUpRequired: false,
    followUpDate: "",
    escalateToAdmin: false,
    notifyUser: true,
    attachments: null as FileList | null,
  });

  const handleResolve = () => {
    // TODO: Resolve issue API call
    console.log("Resolving issue:", issue?.id, resolutionData);
  };

  // Always render modal structure
  if (!issue) {
    return (
      <div className="modal fade" id="resolveIssueModal">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Resolve Issue</h4>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
              />
            </div>
            <div className="modal-body">
              <div className="text-center py-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading issue details...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const resolutionTypes = [
    "Technical Fix Applied",
    "User Training Provided",
    "Configuration Changed",
    "Account Access Restored",
    "Software Update Required",
    "Hardware Replacement",
    "Process Clarification",
    "Policy Exception Granted",
    "Escalated to Development",
    "Third-party Issue",
    "User Error - Guided",
    "Feature Request - Logged",
    "Cannot Reproduce",
    "Duplicate Issue",
    "Other",
  ];

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case "critical":
        return "text-danger";
      case "high":
        return "text-warning";
      case "medium":
        return "text-info";
      case "low":
        return "text-success";
      default:
        return "text-muted";
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category?.toLowerCase()) {
      case "technical":
        return "ti-settings";
      case "account":
        return "ti-user";
      case "payment":
        return "ti-credit-card";
      case "access":
        return "ti-lock";
      case "feature":
        return "ti-bulb";
      default:
        return "ti-help";
    }
  };

  return (
    <div className="modal fade" id="resolveIssueModal">
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title">
              <i className="ti ti-check-circle me-2 text-success"></i>Resolve
              Issue
            </h4>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
            />
          </div>
          <div className="modal-body">
            {/* Issue Summary */}
            <div className="row mb-4">
              <div className="col-md-8">
                <div className="card border">
                  <div className="card-header bg-light">
                    <h6 className="mb-0">
                      <i className="ti ti-file-report me-2"></i>Issue Details
                    </h6>
                  </div>
                  <div className="card-body">
                    <h6 className="mb-2">{issue.title}</h6>
                    <p className="text-muted mb-2">{issue.description}</p>
                    <div className="d-flex flex-wrap gap-2">
                      <span className="badge badge-soft-primary">
                        <i
                          className={`${getCategoryIcon(issue.category)} me-1`}
                        ></i>
                        {issue.category}
                      </span>
                      <span
                        className={`badge badge-soft-${
                          issue.priority === "Critical"
                            ? "danger"
                            : issue.priority === "High"
                            ? "warning"
                            : issue.priority === "Medium"
                            ? "info"
                            : "success"
                        }`}
                      >
                        {issue.priority} Priority
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card border">
                  <div className="card-header bg-light">
                    <h6 className="mb-0">
                      <i className="ti ti-user me-2"></i>Reporter Info
                    </h6>
                  </div>
                  <div className="card-body">
                    <table className="table table-borderless table-sm mb-0">
                      <tbody>
                        <tr>
                          <td>
                            <strong>Name:</strong>
                          </td>
                          <td>{issue.reporterName}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Email:</strong>
                          </td>
                          <td>{issue.reporterEmail}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Role:</strong>
                          </td>
                          <td>{issue.reporterRole}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Ticket:</strong>
                          </td>
                          <td className="text-primary">{issue.ticketId}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Issue Timeline */}
            {issue.timeline && issue.timeline.length > 0 && (
              <div className="mb-4">
                <h6 className="mb-3">
                  <i className="ti ti-history me-2"></i>Issue Timeline
                </h6>
                <div
                  className="timeline-container"
                  style={{ maxHeight: "150px", overflowY: "auto" }}
                >
                  {issue.timeline.map((entry: any, index: number) => (
                    <div key={index} className="d-flex mb-2">
                      <div className="flex-shrink-0 me-3">
                        <i
                          className="ti ti-circle-filled text-primary"
                          style={{ fontSize: "8px" }}
                        ></i>
                      </div>
                      <div className="flex-grow-1">
                        <small className="text-muted">{entry.date}</small>
                        <p className="mb-0 small">{entry.action}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <form>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Resolution Type <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={resolutionData.resolutionType}
                    onChange={(e) =>
                      setResolutionData({
                        ...resolutionData,
                        resolutionType: e.target.value,
                      })
                    }
                    required
                  >
                    <option value="">Select resolution type</option>
                    {resolutionTypes.map((type, index) => (
                      <option key={index} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Time Spent (minutes)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={resolutionData.timeSpent}
                    onChange={(e) =>
                      setResolutionData({
                        ...resolutionData,
                        timeSpent: e.target.value,
                      })
                    }
                    placeholder="e.g., 30"
                    min="1"
                  />
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">
                  Resolution Details <span className="text-danger">*</span>
                </label>
                <textarea
                  className="form-control"
                  rows={4}
                  value={resolutionData.resolution}
                  onChange={(e) =>
                    setResolutionData({
                      ...resolutionData,
                      resolution: e.target.value,
                    })
                  }
                  placeholder="Describe the steps taken to resolve this issue..."
                  required
                />
                <small className="text-muted">
                  Provide detailed information about the resolution for future
                  reference
                </small>
              </div>

              <div className="mb-3">
                <label className="form-label">Supporting Documents</label>
                <input
                  type="file"
                  className="form-control"
                  multiple
                  onChange={(e) =>
                    setResolutionData({
                      ...resolutionData,
                      attachments: e.target.files,
                    })
                  }
                  accept=".pdf,.doc,.docx,.txt,.jpg,.png,.zip"
                />
                <small className="text-muted">
                  Screenshots, logs, or documentation related to the resolution
                </small>
              </div>

              <div className="mb-3">
                <h6 className="mb-2">Follow-up Options</h6>
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="followUpRequired"
                    checked={resolutionData.followUpRequired}
                    onChange={(e) =>
                      setResolutionData({
                        ...resolutionData,
                        followUpRequired: e.target.checked,
                      })
                    }
                  />
                  <label
                    className="form-check-label"
                    htmlFor="followUpRequired"
                  >
                    Schedule follow-up
                  </label>
                </div>

                {resolutionData.followUpRequired && (
                  <div className="mb-3">
                    <label className="form-label">Follow-up Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={resolutionData.followUpDate}
                      onChange={(e) =>
                        setResolutionData({
                          ...resolutionData,
                          followUpDate: e.target.value,
                        })
                      }
                      min={new Date().toISOString().split("T")[0]}
                    />
                  </div>
                )}

                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="escalateToAdmin"
                    checked={resolutionData.escalateToAdmin}
                    onChange={(e) =>
                      setResolutionData({
                        ...resolutionData,
                        escalateToAdmin: e.target.checked,
                      })
                    }
                  />
                  <label className="form-check-label" htmlFor="escalateToAdmin">
                    Escalate to administrator for review
                  </label>
                </div>

                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="notifyUser"
                    checked={resolutionData.notifyUser}
                    onChange={(e) =>
                      setResolutionData({
                        ...resolutionData,
                        notifyUser: e.target.checked,
                      })
                    }
                  />
                  <label className="form-check-label" htmlFor="notifyUser">
                    Send resolution notification to user
                  </label>
                </div>
              </div>

              <div className="alert alert-success">
                <h6>
                  <i className="ti ti-check-circle me-2"></i>Resolution
                  Confirmation
                </h6>
                <p className="mb-0">
                  This issue will be marked as resolved and moved to the closed
                  status. The user will receive a notification with the
                  resolution details.
                </p>
              </div>
            </form>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-light me-2"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-success"
              onClick={handleResolve}
              disabled={
                !resolutionData.resolutionType || !resolutionData.resolution
              }
            >
              <i className="ti ti-check me-2"></i>Resolve Issue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResolveIssueModal;
